// src/components/dashboard/ReviewerPanel.jsx
//
// Reviewer dashboard panel.
// - Lists drafts and covers assigned to this reviewer.
// - Supports Cards view and Table view with search + filters.

import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography, CircularProgress, Modal, Box, TextField, Button,
  Chip, Tooltip, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import DashboardCard from './DashboardCard';
import CoverCard from './CoverCard';
import '../../styles/CoverCard.css';
import { API_BASE } from '../../utils/api';

const ReviewerPanel = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const accessToken = localStorage.getItem('rv_access_token');

  const [pendingBooks, setPendingBooks] = useState([]);
  const [pendingCovers, setPendingCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards');

  // Cover preview modal
  const [selectedCover, setSelectedCover] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Table reject modal
  const [rejectBookTarget, setRejectBookTarget] = useState(null); // { _id, title }
  const [tableRejectNotes, setTableRejectNotes] = useState('');

  // Approve confirmation
  const [approveBookTarget, setApproveBookTarget] = useState(null); // { _id, title }

  // Search + filter — books
  const [bookSearch, setBookSearch] = useState('');
  const [bookAuthorFilter, setBookAuthorFilter] = useState('All');

  // Search + filter — covers
  const [coverSearch, setCoverSearch] = useState('');
  const [coverDesignerFilter, setCoverDesignerFilter] = useState('All');

  const headers = useMemo(() => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  }), [accessToken]);

  const computePdfUrl = (b) => `${API_BASE}/api/drafts/${b._id}/pdf`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const [booksRes, coversRes] = await Promise.all([
          fetch(`${API_BASE}/api/review-queue`, { credentials: 'omit', headers }),
          fetch(`${API_BASE}/api/covers/review-queue`, { credentials: 'omit', headers }),
        ]);
        setPendingBooks(booksRes.ok ? await booksRes.json() : []);
        setPendingCovers(coversRes.ok ? await coversRes.json() : []);
      } catch (e) {
        console.error(e);
        setPendingBooks([]);
        setPendingCovers([]);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchPending();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ───────────────────────────────────────
  const handleApproveBook = async (bookId) => {
    try {
      const res = await fetch(`${API_BASE}/api/review/${bookId}/approve`, { method: 'POST', credentials: 'omit', headers });
      if (res.ok) { setPendingBooks(prev => prev.filter(b => b._id !== bookId)); alert('Book approved!'); }
      else alert('Failed to approve book');
    } catch { alert('Server error'); }
  };

  const handleRejectBook = async (bookId, notes) => {
    try {
      const res = await fetch(`${API_BASE}/api/review/${bookId}/reject`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ notes }),
      });
      if (res.ok) { setPendingBooks(prev => prev.filter(b => b._id !== bookId)); alert('Book rejected.'); }
      else alert('Failed to reject book');
    } catch { alert('Server error'); }
  };

  const handleApproveCover = async (coverId) => {
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/api/covers/${coverId}/approve`, { method: 'POST', credentials: 'omit', headers });
      if (res.ok) { setPendingCovers(prev => prev.filter(c => c._id !== coverId)); setSelectedCover(null); alert('Cover approved!'); }
      else alert('Failed to approve cover');
    } catch { alert('Server error'); }
    finally { setIsProcessing(false); }
  };

  const handleRejectCover = async (coverId) => {
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/api/covers/${coverId}/reject`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ notes: rejectNotes }),
      });
      if (res.ok) { setPendingCovers(prev => prev.filter(c => c._id !== coverId)); setSelectedCover(null); setRejectNotes(''); alert('Cover rejected.'); }
      else alert('Failed to reject cover');
    } catch { alert('Server error'); }
    finally { setIsProcessing(false); }
  };

  // ─── Filter options ──────────────────────────────────
  const bookAuthors    = useMemo(() => ['All', ...new Set(pendingBooks.map(b => b.authorName).filter(Boolean))], [pendingBooks]);
  const coverDesigners = useMemo(() => ['All', ...new Set(pendingCovers.map(c => c.authorName || c.designerName).filter(Boolean))], [pendingCovers]);

  // ─── Filtered data ───────────────────────────────────
  const filteredBooks = useMemo(() => {
    const term = bookSearch.trim().toLowerCase();
    return pendingBooks.filter(b => {
      const matchSearch = !term ||
        (b.title || '').toLowerCase().includes(term) ||
        (b.authorName || '').toLowerCase().includes(term) ||
        (b._id || '').toLowerCase().includes(term);
      const matchAuthor = bookAuthorFilter === 'All' || b.authorName === bookAuthorFilter;
      return matchSearch && matchAuthor;
    });
  }, [pendingBooks, bookSearch, bookAuthorFilter]);

  const filteredCovers = useMemo(() => {
    const term = coverSearch.trim().toLowerCase();
    return pendingCovers.filter(c => {
      const designer = c.authorName || c.designerName || '';
      const matchSearch = !term ||
        (c.title || '').toLowerCase().includes(term) ||
        designer.toLowerCase().includes(term) ||
        (c._id || '').toLowerCase().includes(term);
      const matchDesigner = coverDesignerFilter === 'All' || designer === coverDesignerFilter;
      return matchSearch && matchDesigner;
    });
  }, [pendingCovers, coverSearch, coverDesignerFilter]);

  if (loading) return <CircularProgress />;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <h2 className="section-title" style={{ margin: 0 }}>Reviewer Dashboard</h2>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip label={`${pendingBooks.length} books`} color="primary" variant="outlined" size="small" />
          <Chip label={`${pendingCovers.length} covers`} color="secondary" variant="outlined" size="small" />
          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
            <ToggleButton value="cards"><ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />Cards</ToggleButton>
            <ToggleButton value="table"><TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />Table</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === 'cards' ? (
        // ════════════════════════════════════════════
        // CARDS VIEW
        // ════════════════════════════════════════════
        <>
          <div className="section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#4a5568' }}>📚 Covers Pending Review</h3>
            {pendingCovers.length === 0 ? (
              <Typography variant="body2">No covers pending review.</Typography>
            ) : (
              <div className="cover-grid">
                {pendingCovers.map(cover => (
                  <CoverCard key={cover._id} coverId={cover._id} title={cover.title}
                    authorName={cover.authorName} coverUrl={cover.cover_url}
                    onClick={() => setSelectedCover(cover)} />
                ))}
              </div>
            )}
          </div>

          <div className="section">
            <h3 style={{ marginBottom: '1rem', color: '#4a5568' }}>📖 Books Pending Review</h3>
            {pendingBooks.length === 0 ? (
              <Typography variant="body2">No books pending review.</Typography>
            ) : (
              <div className="tile-grid">
                {pendingBooks.map(book => (
                  <DashboardCard key={book._id} title={book.title} genre={book.genre} volume={book.volume}
                    role="reviewer"
                    openTo={`/review/${book._id}`}
                    openState={{ book: { _id: book._id, title: book.title, pdfUrl: computePdfUrl(book) } }}
                    onApprove={() => handleApproveBook(book._id)}
                    onReject={(notes) => handleRejectBook(book._id, notes)} />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        // ════════════════════════════════════════════
        // TABLE VIEW
        // ════════════════════════════════════════════
        <>
          {/* ── Books Table ── */}
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>📖 Books</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" placeholder="Search title, author, ID..." value={bookSearch}
              onChange={e => setBookSearch(e.target.value)} sx={{ minWidth: 260 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Author</InputLabel>
              <Select value={bookAuthorFilter} label="Author" onChange={e => setBookAuthorFilter(e.target.value)}>
                {bookAuthors.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Author</strong></TableCell>
                  <TableCell><strong>Genre</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBooks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No books match your search.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredBooks.map(book => (
                  <TableRow key={book._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}>
                      <Tooltip title={book._id}><span>{book._id?.slice(-8)}</span></Tooltip>
                    </TableCell>
                    <TableCell>{book.title || '—'}</TableCell>
                    <TableCell>{book.authorName || '—'}</TableCell>
                    <TableCell>{book.genre || '—'}</TableCell>
                    <TableCell>{formatDate(book.lastEdited)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" variant="outlined" href={`/review/${book._id}?type=draft`} target="_blank">Open</Button>
                        <Button size="small" variant="contained" color="success" onClick={() => setApproveBookTarget(book)}>✓</Button>
                        <Button size="small" variant="contained" color="error" onClick={() => setRejectBookTarget(book)}>✗</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Covers Table ── */}
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>🎨 Covers</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" placeholder="Search title, designer, ID..." value={coverSearch}
              onChange={e => setCoverSearch(e.target.value)} sx={{ minWidth: 260 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Designer</InputLabel>
              <Select value={coverDesignerFilter} label="Designer" onChange={e => setCoverDesignerFilter(e.target.value)}>
                {coverDesigners.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Designer</strong></TableCell>
                  <TableCell><strong>Cover Type</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCovers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No covers match your search.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCovers.map(cover => (
                  <TableRow key={cover._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}>
                      <Tooltip title={cover._id}><span>{cover._id?.slice(-8)}</span></Tooltip>
                    </TableCell>
                    <TableCell>{cover.title || '—'}</TableCell>
                    <TableCell>{cover.authorName || cover.designerName || '—'}</TableCell>
                    <TableCell>{cover.coverType || '—'}</TableCell>
                    <TableCell>{formatDate(cover.lastEdited || cover.submittedAt)}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => setSelectedCover(cover)}>Review</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Table Approve Confirmation ─────────── */}
      <Dialog open={!!approveBookTarget} onClose={() => setApproveBookTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve "{approveBookTarget?.title}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will mark the manuscript as approved and notify the author.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveBookTarget(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => {
            handleApproveBook(approveBookTarget._id);
            setApproveBookTarget(null);
          }}>
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Table Reject Modal ─────────────────── */}
      <Dialog open={!!rejectBookTarget} onClose={() => { setRejectBookTarget(null); setTableRejectNotes(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Reject "{rejectBookTarget?.title}"</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline rows={4}
            label="Feedback for author (optional)"
            placeholder="Explain what needs to be revised..."
            value={tableRejectNotes}
            onChange={e => setTableRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectBookTarget(null); setTableRejectNotes(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => {
            handleRejectBook(rejectBookTarget._id, tableRejectNotes);
            setRejectBookTarget(null);
            setTableRejectNotes('');
          }}>
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cover Preview Modal ─────────────────── */}
      <Modal open={!!selectedCover} onClose={() => { setSelectedCover(null); setRejectNotes(''); }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, maxWidth: '95vw', maxHeight: '90vh',
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, overflow: 'auto',
        }}>
          {selectedCover && (
            <>
              <Typography variant="h6" gutterBottom>Review Cover</Typography>
              <img src={selectedCover.cover_url} alt={`Cover for ${selectedCover.title}`}
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }} />
              <Typography variant="subtitle1" fontWeight={600}>{selectedCover.title}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>by {selectedCover.authorName}</Typography>
              <TextField fullWidth multiline rows={3} label="Feedback (optional, shown if rejected)"
                value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                sx={{ mt: 2, mb: 2 }} placeholder="Provide feedback for the author..." />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => { setSelectedCover(null); setRejectNotes(''); }} disabled={isProcessing}>Cancel</Button>
                <Button variant="contained" color="error" onClick={() => handleRejectCover(selectedCover._id)} disabled={isProcessing}>
                  {isProcessing ? 'Processing...' : 'Reject'}
                </Button>
                <Button variant="contained" color="success" onClick={() => handleApproveCover(selectedCover._id)} disabled={isProcessing}>
                  {isProcessing ? 'Processing...' : 'Approve'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ReviewerPanel;
